import React, { useEffect, useState } from 'react';
import { X, TrendingUp, AlertTriangle, Newspaper } from 'lucide-react';
import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';
import type { ScannerResult } from '@/services/scannerService';
import { ModernChart } from '@/components/modern/ModernChart';
import { ErrorBoundary } from '@/components/ui/error-boundary';

/** sessionStorage key the paper-trading order ticket reads once on mount to
 * prefill its symbol — set by the drawer's "Paper trade" button. */
export const TRADE_PREFILL_KEY = 'te.trade.symbol';

/**
 * The click-through every symbol in the app was missing: a right-side drawer
 * with a live chart, the technicals the platform already computes, halt state,
 * news, and a paper-trade shortcut. Opened by ModernDashboard whenever any
 * scanner/dashboard row reports a symbol click (the same onSymbolSelect
 * plumbing that previously only updated the header chip).
 */
export const SymbolDrawer: React.FC<{ symbol: string | null; onClose: () => void }> = ({ symbol, onClose }) => {
  const [data, setData] = useState<ScannerResult | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    if (!symbol) return;
    let alive = true;
    const load = async () => {
      try {
        const res = await apiFetch(`${API_BASE}/api/Scanner/symbol/${encodeURIComponent(symbol)}`);
        if (!alive) return;
        if (!res.ok) { setMissing(true); return; }
        setData(await res.json());
        setMissing(false);
      } catch { if (alive) setMissing(true); }
    };
    void load();
    const t = setInterval(load, 5000);   // keep the numbers live while open
    return () => { alive = false; clearInterval(t); };
  }, [symbol]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!symbol) return null;

  const up = (data?.changePercent ?? 0) >= 0;
  const paperTrade = () => {
    try {
      sessionStorage.setItem(TRADE_PREFILL_KEY, symbol);
      // TradingShell restores its last asset/view from these keys — without forcing
      // them, a user who last left Paper Trading on Forex or Automated lands there
      // and the prefill sits unread. The ticket lives at Equity → Manual.
      localStorage.setItem('trading.paper.asset', 'equity');
      localStorage.setItem('trading.paper.view', 'manual');
    } catch { /* private mode */ }
    window.location.hash = '#/dashboard/paper-trading';
    onClose();
  };

  return (
    <>
      {/* Scrim — click to dismiss. Below the drawer, above everything else. */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside
        role="dialog" aria-label={`${symbol} details`}
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[460px] bg-card border-l shadow-2xl flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-lg font-bold font-mono">{symbol}</span>
              {data && (
                <>
                  <span className="text-lg font-mono tabular-nums">{data.price?.toFixed(data.price < 1 ? 4 : 2)}</span>
                  <span className={`text-sm font-mono tabular-nums ${up ? 'text-emerald-500' : 'text-red-500'}`}>
                    {up ? '+' : ''}{data.changePercent?.toFixed(2)}%
                  </span>
                </>
              )}
              {data?.isHalted && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-500/40 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-500">
                  <AlertTriangle className="h-3 w-3" /> Halted{data.haltReason ? ` · ${data.haltReason}` : ''}
                </span>
              )}
            </div>
            {data?.name && <div className="text-xs text-muted-foreground truncate">{data.name}</div>}
          </div>
          <button onClick={onClose} aria-label="Close"
            className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* overflow-hidden is the guard, chartHeight is the fix: ModernChart
              gives its candlestick container a fixed inline pixel height, so
              without an explicit height it renders its full-page 400px inside
              this box and the canvas spills over the stats below. */}
          <div className="h-[300px] overflow-hidden border-b">
            <ErrorBoundary>
              <ModernChart
                key={symbol}
                symbol={symbol}
                timeframe="1M"
                chartHeight={195}
                className="h-full w-full"
              />
            </ErrorBoundary>
          </div>

          {missing && (
            <div className="p-4 text-sm text-muted-foreground">
              No live data for {symbol} — the platform isn't tracking it right now.
            </div>
          )}

          {data && (
            <div className="p-4 space-y-4">
              {data.hasNews && data.latestHeadline && (
                <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-2.5 text-xs">
                  <Newspaper className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{data.latestHeadline}</span>
                </div>
              )}

              <StatGrid title="Session" stats={[
                ['Open', fmt(data.open)], ['High', fmt(data.high)], ['Low', fmt(data.low)],
                ['Prev close', fmt(data.prevClose)], ['Gap %', pct(data.gapPercent)], ['VWAP', fmt(data.vwap)],
              ]} />
              <StatGrid title="Momentum & volume" stats={[
                ['Volume', vol(data.volume)], ['Rel vol', data.relativeVolume ? `${data.relativeVolume.toFixed(1)}×` : '—'],
                ['2-min %', pct(data.changePercent2Min)], ['ATR', fmt(data.atr)],
                ['ATR %', pct(data.atrPercent)], ['Float', vol(data.float)],
              ]} />
              <StatGrid title="Levels" stats={[
                ['52-wk high', fmt(data.week52High)], ['52-wk low', fmt(data.week52Low)],
                ['SMA 20', fmt(data.dailySma20)], ['SMA 50', fmt(data.dailySma50)],
                ['SMA 200', fmt(data.dailySma200)], ['Mkt cap', vol(data.marketCap)],
              ]} />
            </div>
          )}
        </div>

        <div className="border-t p-3 flex items-center gap-2">
          <button onClick={paperTrade}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <TrendingUp className="h-4 w-4" /> Paper trade {symbol}
          </button>
          <span className="text-[11px] text-muted-foreground">Simulated by default — no broker needed.</span>
        </div>
      </aside>
    </>
  );
};

const fmt = (v?: number | null) => (v == null || v === 0 ? '—' : v.toFixed(v < 1 ? 4 : 2));
const pct = (v?: number | null) => (v == null || v === 0 ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`);
const vol = (v?: number | null) => {
  if (v == null || v === 0) return '—';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(v);
};

function StatGrid({ title, stats }: { title: string; stats: [string, string][] }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
      <div className="grid grid-cols-2 min-[380px]:grid-cols-3 gap-x-4 gap-y-2">
        {stats.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] text-muted-foreground uppercase">{k}</div>
            <div className="text-sm font-mono tabular-nums">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SymbolDrawer;
